/**
 * Arabic document preview / PDF labels — Qawaem ERP (Saudi market).
 * Single source for TemplateWrapper + all *InvoicePreview templates.
 */
import { formatBrandMoney, getBrandCurrencySuffix, getBrandLocale, getBrandVatRate } from '../utils/brandCurrency';

/** Common field / section labels */
export const PREVIEW_LABELS = {
  documentNumber: 'رقم المستند',
  issueDate: 'تاريخ الإصدار',
  validUntil: 'صالح حتى',
  dueDate: 'تاريخ الاستحقاق',
  responseDeadline: 'آخر موعد للرد',
  desiredDelivery: 'التسليم المطلوب',
  processingDeadline: 'آخر موعد للمعالجة',
  expectedReception: 'تاريخ الاستلام المتوقع',
  expectedDelivery: 'تاريخ التسليم المتوقع',
  returnDate: 'تاريخ الإرجاع',
  expectedEntry: 'تاريخ الإدخال المتوقع',
  expectedExit: 'تاريخ الإخراج المتوقع',
  expectedTransfer: 'تاريخ النقل المتوقع',
  shipmentDate: 'تاريخ الشحن',
  expectedCashing: 'تاريخ التحصيل المتوقع',
  paymentDate: 'تاريخ الدفع',
  applicationDate: 'تاريخ التطبيق',
  deadline: 'الاستحقاق',

  client: 'العميل',
  supplier: 'المورد',
  addressee: 'المستلم',
  warehouse: 'المستودع',
  warehouseOrigin: 'مستودع المصدر',
  warehouseDestination: 'مستودع الوجهة',
  documentDetails: 'تفاصيل المستند',
  packageDetails: 'تفاصيل الشحنة',

  operationsDescription: 'وصف العمليات',
  designation: 'البيان',
  quantity: 'الكمية',
  unitPrice: 'سعر الوحدة',
  amount: 'المبلغ',
  totalAmount: 'المبلغ الإجمالي',

  vatNumber: 'الرقم الضريبي',
  artNumber: 'Art N°',
  nisNumber: 'NIS N°',
  commercialRegister: 'السجل التجاري',
  address: 'العنوان',
  phone: 'الهاتف',
  email: 'البريد الإلكتروني',
  unifiedNationalNumber: 'الرقم الموحد للمنشأة',
  taxArticleNumber: 'رقم المادة الضريبية',
  companyLogoAlt: 'شعار الشركة',
  reference: 'المرجع',

  notesAndConditions: 'ملاحظات وشروط',
  supplementaryNotes: 'ملاحظات',
  supplementaryConditions: 'الشروط',
  supplementaryObjective: 'الهدف',
  supplementaryObservations: 'ملاحظات إضافية',
  supplementaryGuarantees: 'الضمانات',
  supplementaryValidity: 'الصلاحية',
  supplementaryMotif: 'السبب',
  supplementaryInstructions: 'تعليمات',
  supplementaryInfo: 'معلومات إضافية',
  noPaymentLines: 'لا توجد بنود دفع',
  paymentLine: 'بند دفع',

  subtotal: 'المجموع الفرعي:',
  commercialDiscount: 'خصم تجاري',
  netCommercial: 'الصافي التجاري:',
  transportFees: 'رسوم الشحن:',
  totalExVat: 'الإجمالي (بدون ضريبة):',
  vat: 'ضريبة القيمة المضافة',
  totalIncVat: 'الإجمالي (شامل الضريبة):',
  downPayment: 'دفعة مقدمة:',
  advance: 'سلفة:',
  guaranteeHold: 'ضمان محتجز:',
  returnCost: 'تكلفة الإرجاع:',
  netPayable: 'صافي المستحق:',

  receptionIndicativeValue: 'قيمة استلامية (بدون ضريبة):',

  amountClosingPrefix: 'يُوقَف',
  amountClosingSuffix: 'بمبلغ',
  amountClosingOnly: 'فقط لا غير',

  signatureApproval: 'موافقة',
  signaturePrepared: 'أُعدّ بواسطة',
  signatureReceived: 'استلم',
  signatureAndStamp: 'التوقيع والختم',
  signatureDate: 'التاريخ',
  signaturePlace: 'المكان',
  signatureName: 'الاسم',
  signatureRole: 'الدور',

  packageType: 'نوع الطرد',
  packageCount: 'عدد الطرود',
  totalWeight: 'الوزن الإجمالي',
  volume: 'الحجم',
  declaredValue: 'القيمة المصرّح بها',
  managerName: 'اسم المدير',
  departureTime: 'وقت المغادرة',
  arrivalTime: 'وقت الوصول',
  internalRef: 'المرجع الداخلي',
  locationShort: 'المكان',
  productType: 'نوع المنتج',
  itemDefault: 'صنف',
  totalShort: 'الإجمالي',
  amountHeader: 'المبلغ',
  paymentReason: 'السبب',
  responsible: 'المسؤول',
  issuedOnPrefix: 'تاريخ الإصدار:',
  dueOnPrefix: 'تاريخ الاستحقاق:',
  continuationLinesOnPreviousPage: 'تفاصيل البنود في الصفحة السابقة.',

  headquarters: 'المقر',
  headquartersAddress: 'المقر:',
  website: 'الموقع الإلكتروني:',
  bankAccountsHeader: 'الحسابات البنكية رقم:',
  ccpAccountHeader: 'CCP رقم:',
  bankAccounts: 'الحسابات البنكية',
  defaultPaymentMethod: 'تحويل بنكي',
  fiscalStamp: 'الطابع الجبائي:',
  companyStampAlt: 'ختم الشركة',
  companySignatureAlt: 'توقيع الشركة',
};

/** Normalize form slugs (accents, hyphens, spaces) for PREVIEW_ENUM_VALUES lookup. */
export function normalizePreviewSlugKey(v) {
  if (v == null || v === '') return '';
  return String(v)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[''`]/g, '')
    .replace(/-/g, '_')
    .replace(/[\s–—]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export function mapPreviewEnumValue(value, map) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const candidates = [
    normalizePreviewSlugKey(raw),
    raw.toLowerCase().replace(/[\s-]+/g, '_'),
    raw.toLowerCase(),
  ];
  for (const key of candidates) {
    if (key && map[key]) return map[key];
  }
  if (/[_]/.test(raw)) {
    return raw
      .split(/_+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  return raw;
}

export const PREVIEW_ENUM_VALUES = {
  orderOrigin: {
    'site-web': 'الموقع الإلكتروني',
    site_web: 'الموقع الإلكتروني',
    telephone: 'الهاتف',
    email: 'البريد الإلكتروني',
    visite: 'زيارة العميل',
    marketplace: 'منصة تجارية',
    recommandation: 'توصية',
    autre: 'أخرى',
  },
  paymentTypeInvoice: {
    total: 'دفع كامل',
    partial: 'دفع جزئي',
    advance: 'دفعة مقدمة',
    installment: 'دفع على أقساط',
  },
  invoiceType: {
    vente: 'فاتورة مبيعات',
    acompte: 'فاتورة دفعة مقدمة',
    solde: 'فاتورة الرصيد',
    rectificative: 'فاتورة تصحيحية',
    annulation: 'فاتورة إلغاء',
  },
  deliveryMethod: {
    standard: 'قياسي',
    express: 'سريع',
    retrait: 'استلام من المقر',
    coursier: 'مندوب توصيل',
  },
  goodsCondition: {
    neuf: 'جديد',
    bon_etat: 'حالة جيدة',
    etat_moyen: 'حالة متوسطة',
    endommage: 'تالف',
    endommagee: 'تالف',
    inutilisable: 'غير صالح للاستخدام',
    a_recycler: 'للتدوير',
    emballage_original: 'العبوة الأصلية',
    emballage_d_origine: 'العبوة الأصلية',
    emballage_endommage: 'عبوة تالفة',
    produit_endommage: 'منتج تالف',
    defectueux: 'معيب',
    incomplet: 'ناقص',
    utilise: 'مستعمل',
  },
  returnType: {
    retour_marchandise: 'إرجاع بضائع',
    retour_garantie: 'إرجاع ضمان',
    retour_sav: 'إرجاع خدمة ما بعد البيع',
    echange: 'استبدال',
    remboursement: 'استرداد',
    avoir: 'إشعار دائن',
    reparation: 'إصلاح',
  },
  returnReason: {
    defectueux: 'منتج معيب',
    produit_defectueux: 'منتج معيب',
    non_conforme: 'غير مطابق',
    erreur_livraison: 'خطأ في التسليم',
    erreur_de_livraison: 'خطأ في التسليم',
    endommage: 'تالف أثناء النقل',
    produit_endommage: 'منتج تالف',
    endommage_transport: 'تالف أثناء النقل',
    fin_contrat: 'انتهاء العقد',
    changement_besoin: 'تغيير في الاحتياج',
    garantie: 'تحت الضمان',
    surstockage: 'فائض مخزون',
    perime: 'منتج منتهي الصلاحية',
    erreur_de_commande: 'خطأ في الطلب',
    fin_de_garantie: 'انتهاء الضمان',
    emballage_original: 'العبوة الأصلية',
    emballage_d_origine: 'العبوة الأصلية',
    autre: 'أخرى',
  },
  projectType: {
    sur_mesure: 'حسب الطلب',
    standard: 'قياسي',
    maintenance: 'صيانة',
    installation: 'تركيب',
    formation: 'تدريب',
    consulting: 'استشارات',
  },
  paymentTerms: {
    comptant: 'نقداً',
    '30_jours_fin_mois': '30 يوماً نهاية الشهر',
    '30_jours_nets': '30 يوماً صافية',
    '45_jours_fin_mois': '45 يوماً نهاية الشهر',
    '60_jours_fin_mois': '60 يوماً نهاية الشهر',
    sur_facturation: 'على الفاتورة',
    echelonne: 'دفع على أقساط',
    '30_jours': '30 يوماً',
    '60_jours': '60 يوماً',
    '90_jours': '90 يوماً',
    '50_50': '50% عند الطلب، 50% عند التسليم',
    echeance: 'عند الاستحقاق',
    immediat: 'فوري',
  },
  quotePaymentTerms: {
    comptant: 'نقداً',
    '15': '15 يوماً',
    '30': '30 يوماً',
    '45': '45 يوماً',
    '60': '60 يوماً',
    '90': '90 يوماً',
    acompte_30: '30% عند الطلب، الرصيد عند التسليم',
    acompte_50: '50% عند الطلب، الرصيد عند التسليم',
  },
  receptionStockDestination: {
    entrepot_principal: 'المستودع الرئيسي',
    entrepot_secondaire: 'مستودع ثانوي',
    quarantaine: 'حجر',
    retour_fournisseur: 'إرجاع للمورد',
    mise_au_rebut: 'إتلاف',
  },
  receptionQualityCompliance: {
    conforme: 'مطابق',
    non_conforme: 'غير مطابق',
    a_verifier: 'يحتاج مراجعة',
    avec_reserves: 'مع تحفظات',
  },
  receptionQuantityCompliance: {
    conforme: 'مطابق',
    surplus: 'فائض',
    manquant: 'ناقص',
    partiel: 'تسليم جزئي',
  },
  receptionOverallCondition: {
    excellent: 'ممتاز',
    bon: 'جيد',
    satisfaisant: 'مرضٍ',
    defectueux: 'معيب',
    endommage: 'تالف',
  },
  receptionCarrier: {
    smsa: 'SMSA',
    aramex: 'Aramex',
    dhl: 'DHL',
    'saudi post': 'البريد السعودي',
    fedex: 'FedEx',
    ups: 'UPS',
    autre: 'ناقل آخر',
  },
  stockEntrySource: {
    supplier: 'المورد',
    production: 'إنتاج داخلي',
    transfer: 'نقل بين المستودعات',
    return: 'إرجاع عميل',
    adjustment: 'تعديل مخزون',
    other: 'أخرى',
  },
  stockEntryCondition: {
    good: 'حالة جيدة',
    excellent: 'ممتاز',
    damaged: 'تالف',
    partial: 'جزئي',
    quarantine: 'في الحجر',
    defective: 'معيب',
  },
  exitOrTransferDestination: {
    client: 'العميل',
    fournisseur: 'المورد',
    depot: 'مستودع آخر',
    production: 'الإنتاج',
    externe: 'خارجي',
  },
  logisticsTransportMode: {
    camion: 'شاحنة',
    fourgonnette: 'فان',
    coursier: 'مندوب',
    palette: 'منصة',
    transport_express: 'نقل سريع',
    livraison_standard: 'تسليم قياسي',
    transport_interne: 'نقل داخلي',
    chariot: 'رافعة شوكية',
    route: 'براً',
    rail: 'سكة حديد',
    aerien: 'جوي',
    aérien: 'جوي',
    maritime: 'بحري',
    poste: 'بريد',
  },
  settlementType: {
    total: 'تسوية كاملة',
    partiel: 'تسوية جزئية',
    acompte: 'دفعة مقدمة',
    solde: 'الرصيد النهائي',
  },
  refundMethod: {
    account_credit: 'رصيد على حساب العميل',
    bank_transfer: 'تحويل بنكي',
    virement: 'تحويل بنكي',
    check: 'شيك',
    cheque: 'شيك',
    cash: 'نقداً',
    especes: 'نقداً',
    card: 'بطاقة',
    carte: 'بطاقة',
    compensation: 'مقاصة',
  },
  creditNoteType: {
    retour: 'إرجاع بضائع',
    remise: 'خصم تجاري',
    correction: 'تصحيح خطأ',
    annulation: 'إلغاء جزئي',
    defaut: 'عدم مطابقة',
    ristourne: 'خصم نهاية السنة',
  },
};

const DOCUMENT_TITLES = {
  'quote-request': 'طلب عرض أسعار',
  'proforma-request': 'طلب فاتورة أولية',
  'purchase-order': 'أمر شراء',
  'payment-voucher': 'سند صرف',
  'payment_slip': 'سند صرف',
  'return-slip': 'إشعار إرجاع',
  quote: 'عرض سعر',
  proforma: 'فاتورة أولية',
  invoice: 'فاتورة',
  'material-exit': 'بون تسليم',
  'delivery-note': 'بون تسليم',
  delivery: 'بون تسليم',
  'client-return-slip': 'إشعار إرجاع عميل',
  'credit-note': 'إشعار دائن',
  credit_note: 'إشعار دائن',
  'payment-receipt': 'سند قبض',
  payment_receipt: 'سند قبض',
  'reception-note': 'إشعار استلام',
  receipt_note: 'إشعار استلام',
  'exit-note': 'إشعار صرف',
  exit_slip: 'إشعار صرف',
  'transfer-note': 'إشعار نقل',
  transfer_slip: 'إشعار نقل',
  'stock-entry': 'إشعار إدخال',
  'shipping-slip': 'بوليصة شحن',
  shipping: 'بوليصة شحن',
};

const DOCUMENT_TYPE_PHRASE = {
  'quote-request': 'طلب عرض الأسعار',
  'proforma-request': 'طلب الفاتورة الأولية',
  'purchase-order': 'أمر الشراء',
  'payment-voucher': 'سند الصرف',
  'return-slip': 'إشعار الإرجاع',
  quote: 'عرض السعر',
  proforma: 'الفاتورة الأولية',
  invoice: 'الفاتورة',
  'material-exit': 'بون التسليم',
  'delivery-note': 'بون التسليم',
  delivery: 'بون التسليم',
  'client-return-slip': 'إشعار إرجاع العميل',
  'credit-note': 'الإشعار الدائن',
  credit_note: 'الإشعار الدائن',
  'payment-receipt': 'سند القبض',
  payment_receipt: 'سند القبض',
  'reception-note': 'إشعار الاستلام',
  'exit-note': 'إشعار الصرف',
  'transfer-note': 'إشعار النقل',
  'stock-entry': 'إشعار الإدخال',
  'shipping-slip': 'بوليصة الشحن',
  shipping: 'بوليصة الشحن',
};

const PAYMENT_METHOD_LABELS = {
  virement: 'تحويل بنكي',
  cheque: 'شيك',
  especes: 'نقداً',
  carte: 'بطاقة',
  effet: 'كمبيالة',
  echeances: 'دفع على أقساط',
  traite: 'كمبيالة',
  compensation: 'مقاصة',
  bank_transfer: 'تحويل بنكي',
  cash: 'نقداً',
  card: 'بطاقة',
  mada: 'مدى',
};

const PRIORITY_LABELS = {
  urgent: 'عاجلة',
  urgente: 'عاجلة',
  high: 'مرتفعة',
  haute: 'مرتفعة',
  normal: 'عادية',
  normale: 'عادية',
  low: 'منخفضة',
  basse: 'منخفضة',
};

export function normalizePreviewDocType(type) {
  return String(type ?? '')
    .trim()
    .replace(/_/g, '-')
    .toLowerCase();
}

export function getDocumentPreviewTitle(type, creditNotePdf = null) {
  const dt = normalizePreviewDocType(type);
  if (dt === 'credit-note' || dt === 'credit_note') {
    return creditNotePdf?.titleUpper || 'إشعار دائن';
  }
  return DOCUMENT_TITLES[dt] || DOCUMENT_TITLES[type] || 'مستند';
}

export function getDocumentTypePhrase(type, creditNotePdf = null) {
  const dt = normalizePreviewDocType(type);
  if (dt === 'credit-note' || dt === 'credit_note') {
    return creditNotePdf?.titleFr || DOCUMENT_TYPE_PHRASE[dt] || 'الإشعار الدائن';
  }
  return DOCUMENT_TYPE_PHRASE[dt] || DOCUMENT_TYPE_PHRASE[type] || 'المستند';
}

export function formatPreviewDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(getBrandLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatPreviewPaymentMethod(pm) {
  if (!pm) return '';
  const key = String(pm).toLowerCase().replace(/\s+/g, '_');
  return PAYMENT_METHOD_LABELS[key] || PAYMENT_METHOD_LABELS[String(pm).toLowerCase()] || String(pm);
}

export function formatPreviewPriority(value) {
  if (!value) return '';
  const key = String(value).toLowerCase();
  return PRIORITY_LABELS[key] || String(value);
}

export function getPartnerBillToTitle(partnerType, documentPartnerLabel) {
  if (documentPartnerLabel) return documentPartnerLabel;
  return partnerType === 'supplier' ? PREVIEW_LABELS.supplier : PREVIEW_LABELS.client;
}

export function getPartnerLabelShort(partnerType) {
  return partnerType === 'supplier' ? PREVIEW_LABELS.supplier : PREVIEW_LABELS.client;
}

export function getDocumentDetailsLabelForType(documentType) {
  const dt = normalizePreviewDocType(documentType);
  if (dt === 'transfer-note') return PREVIEW_LABELS.warehouseDestination;
  if (dt === 'shipping-slip' || dt === 'shipping') return PREVIEW_LABELS.packageDetails;
  return PREVIEW_LABELS.documentDetails;
}

export function getDocumentPartnerLabelForType(documentType, partnerType, formData = {}) {
  const dt = normalizePreviewDocType(documentType);
  if (partnerType === 'supplier') return PREVIEW_LABELS.supplier;
  if (dt === 'transfer-note') return PREVIEW_LABELS.warehouseOrigin;
  if (dt === 'exit-note') {
    const pType = formData.exitNotePartnerType || 'client';
    if (pType === 'supplier') return PREVIEW_LABELS.supplier;
    if (pType === 'warehouse') return PREVIEW_LABELS.warehouse;
    if (pType === 'other') return PREVIEW_LABELS.addressee;
  }
  return PREVIEW_LABELS.client;
}

export function numberToBrandWords(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return `صفر ${getBrandCurrencySuffix()} ${PREVIEW_LABELS.amountClosingOnly}`;
  return `${formatBrandMoney(n)} ${getBrandCurrencySuffix()} ${PREVIEW_LABELS.amountClosingOnly}`;
}

export function buildAmountClosingLine(documentTypePhrase, amount) {
  const words = numberToBrandWords(amount);
  return `${PREVIEW_LABELS.amountClosingPrefix} ${documentTypePhrase} ${PREVIEW_LABELS.amountClosingSuffix}: ${words}`;
}

export function getTotalsRowLabels() {
  return {
    subtotal: PREVIEW_LABELS.subtotal,
    netCommercial: PREVIEW_LABELS.netCommercial,
    transport: PREVIEW_LABELS.transportFees,
    totalExVat: PREVIEW_LABELS.totalExVat,
    totalIncVat: PREVIEW_LABELS.totalIncVat,
    downPayment: PREVIEW_LABELS.downPayment,
    advance: PREVIEW_LABELS.advance,
    guaranteeHold: PREVIEW_LABELS.guaranteeHold,
    returnCost: PREVIEW_LABELS.returnCost,
    netPayable: PREVIEW_LABELS.netPayable,
    vat: PREVIEW_LABELS.vat,
    vatRate: getBrandVatRate(),
    receptionIndicative: PREVIEW_LABELS.receptionIndicativeValue,
    commercialDiscount: (rate) => `${PREVIEW_LABELS.commercialDiscount} (${rate}%):`,
    rabais: 'خصم/ريبيت:',
    fiscalStamp: PREVIEW_LABELS.fiscalStamp,
    escompte: (rate) => `خصم (${rate}%):`,
    tap: (rate) => `TAP (${rate}%):`,
    irgWithholding: (rate) => `IRG - اقتطاع عند المصدر (${rate}%):`,
    ibsWithholding: (rate) => `IBS - اقتطاع عند المصدر (${rate}%):`,
  };
}

/** Map legacy French header/detail keys → Arabic (TemplateWrapper row builders). */
export const HEADER_LABEL_MAP = {
  'N° document': PREVIEW_LABELS.documentNumber,
  "Date d'émission": PREVIEW_LABELS.issueDate,
  'Date limite de réponse': PREVIEW_LABELS.responseDeadline,
  'Livraison souhaitée': PREVIEW_LABELS.desiredDelivery,
  'Date limite de traitement': PREVIEW_LABELS.processingDeadline,
  "Valable jusqu'au": PREVIEW_LABELS.validUntil,
  "Date d'échéance": PREVIEW_LABELS.dueDate,
  'Date de réception prévue': PREVIEW_LABELS.expectedReception,
  'Date de livraison prévue': PREVIEW_LABELS.expectedDelivery,
  'Date de retour': PREVIEW_LABELS.returnDate,
  "Date d'entrée prévue": PREVIEW_LABELS.expectedEntry,
  'Date de sortie prévue': PREVIEW_LABELS.expectedExit,
  'Date de transfert prévue': PREVIEW_LABELS.expectedTransfer,
  "Date d'expédition": PREVIEW_LABELS.shipmentDate,
  "Date d'encaissement prévue": PREVIEW_LABELS.expectedCashing,
  'Date de paiement': PREVIEW_LABELS.paymentDate,
  "Date d'application": PREVIEW_LABELS.applicationDate,
  Échéance: PREVIEW_LABELS.deadline,

  Priorité: 'الأولوية',
  'Mode de paiement': 'طريقة الدفع',
  'Établi par': 'أُعدّ بواسطة',
  'Motif du paiement': 'سبب الدفع',
  'Transaction / chèque': 'رقم المعاملة / الشيك',
  'Réf. avoir sur achat': 'مرجع إشعار دائن (مورد)',
  'Réf. facture fournisseur': 'مرجع فاتورة المورد',
  'Banque émetteur': 'البنك المُصدِر',
  'Compte émetteur': 'حساب المُصدِر',
  'Banque réceptrice': 'البنك المستلم',
  'Compte récepteur': 'حساب المستلم',
  'Commercial responsable': 'المسؤول التجاري',
  'Type de facture': 'نوع الفاتورة',
  'Réf. bon de commande': 'مرجع أمر الشراء',
  'Réf. commande client': 'مرجع طلب العميل',
  'Réf. projet/contrat': 'مرجع المشروع/العقد',
  'Réf. bon de livraison': 'مرجع بون التسليم',
  'Lieu de livraison': 'مكان التسليم',
  'Date de livraison': 'تاريخ التسليم',
  Méthode: 'الطريقة',
  Acheteur: 'المشتري',
  'Type de projet': 'نوع المشروع',
  'Délai de livraison': 'مهلة التسليم',
  'Méthode de paiement': 'طريقة الدفع',
  'Conditions de paiement': 'شروط الدفع',
  Statut: 'الحالة',
  Référence: 'المرجع',
  Objet: 'الموضوع',
  'N° document': PREVIEW_LABELS.documentNumber,
  'Date de livraison': 'تاريخ التسليم',
  "Date d'échéance du paiement": 'تاريخ استحقاق الدفع',
  'Réf. demande de devis': 'مرجع طلب عرض الأسعار',
  'Réf. demande de proforma': 'مرجع طلب الفاتورة الأولية',
  'Montant facturé initial': 'المبلغ المفوتر الأولي',
  'Montant facture fournisseur': 'مبلغ فاتورة المورد',
  'Montant facture': 'مبلغ الفاتورة',
  'Approuvé par': 'اعتمد بواسطة',
  'Informations commerciales': 'معلومات تجارية',
  'Projet & délais': 'المشروع والمواعيد',
  'Livraison & Paiement': 'التسليم والدفع',
  'Références et traçabilité': 'المراجع والتتبع',
  'Modalités financières': 'الشروط المالية',
  'Références': 'المراجع',
  'Origine & Destination': 'المصدر والوجهة',
  'Expédition': 'الشحن',
  'Détails du paiement': 'تفاصيل الدفع',
  'Références factures': 'مراجع الفواتير',
  'Conditions de la demande': 'شروط الطلب',
  'Bon de commande': 'أمر الشراء',
  'Informations du retour': 'معلومات الإرجاع',
  'Contrôle et traitement': 'المراقبة والمعالجة',
  'Informations de sortie': 'معلومات الصرف',
  'Autorisation et contrôle': 'التفويض والمراقبة',
  'Informations de transfert': 'معلومات النقل',
  'Autorisation et suivi': 'التفويض والمتابعة',
  "Informations d'entrée": 'معلومات الإدخال',
  'Réception et contrôle': 'الاستلام والمراقبة',
  "Informations d'expédition": 'معلومات الشحن',
  'Facture fournisseur': 'فاتورة المورد',
  'Réf. facture': 'مرجع الفاتورة',
  'Délai de réalisation': 'مهلة التنفيذ',
  'Budget maximum estimé': 'الحد الأقصى للميزانية التقديرية',
  'Documents requis': 'المستندات المطلوبة',
  'Mode de livraison': 'طريقة التسليم',
  'Contrôlé par': 'راجع بواسطة',
  'Date facture fournisseur': 'تاريخ فاتورة المورد',
  "Entrepôt d'origine": 'مستودع المصدر',
  'Type de retour': 'نوع الإرجاع',
  'Motif de retour': 'سبب الإرجاع',
  'Transporteur retour': 'ناقل الإرجاع',
  'État des marchandises': 'حالة البضائع',
  'Numéro de suivi transporteur': 'رقم تتبع الناقل',
  "Numéro d'autorisation de retour": 'رقم ترخيص الإرجاع',
  "Type d'avoir": 'نوع الإشعار الدائن',
  'Entrepôt de réception': 'مستودع الاستلام',
  'Adresse de réception': 'عنوان الاستلام',
  'Destination stock': 'وجهة المخزون',
  Transporteur: 'الناقل',
  'Heure de réception': 'وقت الاستلام',
  'État général': 'الحالة العامة',
  'Conformité quantité': 'مطابقة الكمية',
  'Conformité qualité': 'مطابقة الجودة',
  'Entrepôt de destination': 'مستودع الوجهة',
  "Lieu d'exécution/livraison": 'مكان التنفيذ/التسليم',
  "Modalités d'acompte": 'شروط الدفعة المقدمة',
  'Origine de la commande': 'مصدر الطلب',
  'Type de paiement': 'نوع الدفع',
  "Conditions d'escompte": 'شروط الخصم',
  'Clause de réserve de propriété': 'بند حجز الملكية',
  'Réf. devis': 'مرجع عرض السعر',
  'Entrepôt de départ': 'مستودع المغادرة',
  'N° de suivi': 'رقم التتبع',
  'Nombre de colis': 'عدد الطرود',
  'Référence de paiement': 'مرجع الدفع',
  'Type de règlement': 'نوع التسوية',
  'Banque émettrice': 'البنك المُصدِر',
  'Solde restant': 'الرصيد المتبقي',
  'Mode de remboursement': 'طريقة الاسترداد',
  'Paiement souhaité': 'الدفع المطلوب',
  'Délai de validité': 'مهلة الصلاحية',
  'Modalités de livraison': 'شروط التسليم',
  'Bon de commande client': 'أمر شراء العميل',
  Facture: 'الفاتورة',
  'Bon de livraison': 'بون التسليم',
  'Bon de transfert': 'بون النقل',
  'Référence document': 'مرجع المستند',
  Destination: 'الوجهة',
  'Mode de transport': 'وسيلة النقل',
  'Responsable de la sortie': 'مسؤول الصرف',
  'Heure de sortie': 'وقت الصرف',
  "Source de l'entrée": 'مصدر الإدخال',
  'Bon de commande fournisseur': 'أمر شراء المورد',
  'Bon de réception': 'إشعار الاستلام',
  'Reçu par': 'استلم بواسطة',
  'État de la marchandise': 'حالة البضاعة',
  'Réf. bon de commande client': 'مرجع أمر شراء العميل',
  'Demandé par': 'طُلب بواسطة',
  'Responsable du transfert': 'مسؤول النقل',
  'Heure de départ prévue': 'وقت المغادرة المتوقع',
  "Heure d'arrivée prévue": 'وقت الوصول المتوقع',
  'Numéro de suivi': 'رقم التتبع',
  'Demande de transfert': 'طلب النقل',
  'Date facture': 'تاريخ الفاتورة',
  'Réf. facture fournisseur': 'مرجع فاتورة المورد',
  'Réf. bon de commande client': 'مرجع أمر شراء العميل',
  "Informations d'expédition": 'معلومات الشحن',
};

/** Payment voucher reason slugs + legacy French labels → Arabic (preview/PDF). */
const PAYMENT_VOUCHER_REASON_LABELS = {
  facture_fournisseur: 'دفع فاتورة مورد',
  bon_commande: 'دفع أمر شراء',
  acompte: 'دفعة مقدمة للمورد',
  solde: 'رصيد الطلب',
  avance: 'سلفة للمورد',
  regularisation: 'تسوية',
  remboursement: 'استرداد (إشعار دائن)',
  facture: 'دفع فاتورة مورد',
  paiement_facture: 'دفع فاتورة مورد',
  'paiement facture': 'دفع فاتورة مورد',
  'Paiement facture fournisseur': 'دفع فاتورة مورد',
  'Paiement bon de commande': 'دفع أمر شراء',
  'Acompte fournisseur': 'دفعة مقدمة للمورد',
  'Solde commande': 'رصيد الطلب',
  'Avance fournisseur': 'سلفة للمورد',
  Régularisation: 'تسوية',
  'Remboursement (avoir)': 'استرداد (إشعار دائن)',
};

export function formatPreviewPaymentVoucherReason(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (PAYMENT_VOUCHER_REASON_LABELS[s]) return PAYMENT_VOUCHER_REASON_LABELS[s];
  const slug = normalizePreviewSlugKey(s);
  if (PAYMENT_VOUCHER_REASON_LABELS[slug]) return PAYMENT_VOUCHER_REASON_LABELS[slug];
  const lower = s.toLowerCase();
  if (PAYMENT_VOUCHER_REASON_LABELS[lower]) return PAYMENT_VOUCHER_REASON_LABELS[lower];
  return s;
}

/** Field labels with trailing colon (partner/shipment blocks in *InvoicePreview). */
export const PREVIEW_FIELD_LABELS = {
  reference: `${PREVIEW_LABELS.reference}:`,
  internalRef: `${PREVIEW_LABELS.internalRef}:`,
  managerName: `${PREVIEW_LABELS.managerName}:`,
  departureTime: `${PREVIEW_LABELS.departureTime}:`,
  arrivalTime: `${PREVIEW_LABELS.arrivalTime}:`,
  packageType: `${PREVIEW_LABELS.packageType}:`,
  packageCount: `${PREVIEW_LABELS.packageCount}:`,
  totalWeight: `${PREVIEW_LABELS.totalWeight}:`,
  volume: `${PREVIEW_LABELS.volume}:`,
  declaredValue: `${PREVIEW_LABELS.declaredValue}:`,
  productType: `${PREVIEW_LABELS.productType}:`,
  description: `${PREVIEW_LABELS.designation}:`,
  motif: `${PREVIEW_LABELS.paymentReason}:`,
  responsible: `${PREVIEW_LABELS.responsible}:`,
  locationShort: `${PREVIEW_LABELS.locationShort}:`,
  vatNumber: `${PREVIEW_LABELS.vatNumber}:`,
  artNumber: `${PREVIEW_LABELS.artNumber}:`,
  nisNumber: `${PREVIEW_LABELS.nisNumber}:`,
  commercialRegister: `${PREVIEW_LABELS.commercialRegister}:`,
  address: `${PREVIEW_LABELS.address}:`,
  phone: `${PREVIEW_LABELS.phone}:`,
  email: `${PREVIEW_LABELS.email}:`,
  unifiedNationalNumber: `${PREVIEW_LABELS.unifiedNationalNumber}:`,
  taxArticleNumber: `${PREVIEW_LABELS.taxArticleNumber}:`,
};

export function translatePreviewRowLabel(label) {
  if (!label) return label;
  return HEADER_LABEL_MAP[label] || label;
}

/** Section card titles in buildDocumentInfoSections (same map as row labels). */
export function translatePreviewSectionTitle(title) {
  return translatePreviewRowLabel(title);
}

/** Canonical English keys for supplementary section toggles (preview / archives). */
export const SUPPLEMENTARY_SECTIONS = {
  notes: 'notes',
  conditions: 'conditions',
  objective: 'objective',
  observations: 'observations',
  guarantees: 'guarantees',
  validity: 'validity',
  motif: 'motif',
  instructions: 'instructions',
};

export const DEFAULT_SUPPLEMENTARY_SECTION_ORDER = [
  SUPPLEMENTARY_SECTIONS.notes,
  SUPPLEMENTARY_SECTIONS.conditions,
  SUPPLEMENTARY_SECTIONS.objective,
  SUPPLEMENTARY_SECTIONS.observations,
  SUPPLEMENTARY_SECTIONS.guarantees,
  SUPPLEMENTARY_SECTIONS.validity,
  SUPPLEMENTARY_SECTIONS.motif,
];

/** Legacy French section keys persisted in validityNotes / sectionOrder. */
const LEGACY_SUPPLEMENTARY_SECTION_KEY_MAP = {
  Notes: SUPPLEMENTARY_SECTIONS.notes,
  Conditions: SUPPLEMENTARY_SECTIONS.conditions,
  Objectif: SUPPLEMENTARY_SECTIONS.objective,
  Observations: SUPPLEMENTARY_SECTIONS.observations,
  Garanties: SUPPLEMENTARY_SECTIONS.guarantees,
  Validité: SUPPLEMENTARY_SECTIONS.validity,
  Validite: SUPPLEMENTARY_SECTIONS.validity,
  Motif: SUPPLEMENTARY_SECTIONS.motif,
  Instructions: SUPPLEMENTARY_SECTIONS.instructions,
};

export function normalizeSupplementarySectionKey(section) {
  if (section == null || section === '') return section;
  const raw = String(section).trim();
  if (Object.values(SUPPLEMENTARY_SECTIONS).includes(raw)) return raw;
  return LEGACY_SUPPLEMENTARY_SECTION_KEY_MAP[raw] || raw.toLowerCase();
}

export const SUPPLEMENTARY_SECTION_SHOW_FIELDS = {
  [SUPPLEMENTARY_SECTIONS.notes]: 'showNotes',
  [SUPPLEMENTARY_SECTIONS.conditions]: 'showConditions',
  [SUPPLEMENTARY_SECTIONS.objective]: 'showObjectif',
  [SUPPLEMENTARY_SECTIONS.observations]: 'showObservations',
  [SUPPLEMENTARY_SECTIONS.guarantees]: 'showGaranties',
  [SUPPLEMENTARY_SECTIONS.validity]: 'showValidite',
  [SUPPLEMENTARY_SECTIONS.motif]: 'showMotif',
  [SUPPLEMENTARY_SECTIONS.instructions]: 'showInstructions',
};

export function getSupplementarySectionShowField(section) {
  const key = normalizeSupplementarySectionKey(section);
  return SUPPLEMENTARY_SECTION_SHOW_FIELDS[key] || null;
}

export function getSupplementarySectionPreviewLabel(section) {
  const key = normalizeSupplementarySectionKey(section);
  const labels = {
    [SUPPLEMENTARY_SECTIONS.notes]: PREVIEW_LABELS.supplementaryNotes,
    [SUPPLEMENTARY_SECTIONS.conditions]: PREVIEW_LABELS.supplementaryConditions,
    [SUPPLEMENTARY_SECTIONS.objective]: PREVIEW_LABELS.supplementaryObjective,
    [SUPPLEMENTARY_SECTIONS.observations]: PREVIEW_LABELS.supplementaryObservations,
    [SUPPLEMENTARY_SECTIONS.guarantees]: PREVIEW_LABELS.supplementaryGuarantees,
    [SUPPLEMENTARY_SECTIONS.validity]: PREVIEW_LABELS.supplementaryValidity,
    [SUPPLEMENTARY_SECTIONS.motif]: PREVIEW_LABELS.supplementaryMotif,
    [SUPPLEMENTARY_SECTIONS.instructions]: PREVIEW_LABELS.supplementaryInstructions,
  };
  return labels[key] || PREVIEW_LABELS.supplementaryInfo;
}

export function normalizeSupplementarySectionOrder(order) {
  const base = Array.isArray(order) && order.length ? order : DEFAULT_SUPPLEMENTARY_SECTION_ORDER;
  return base.map(normalizeSupplementarySectionKey);
}

export function translatePartnerSectionLabel(label) {
  const u = String(label || '').toUpperCase().trim();
  if (u === 'CLIENT') return PREVIEW_LABELS.client;
  if (u === 'FOURNISSEUR') return PREVIEW_LABELS.supplier;
  if (u === 'DESTINATAIRE') return PREVIEW_LABELS.addressee;
  if (u === 'ENTREPÔT') return PREVIEW_LABELS.warehouse;
  if (u.includes('ORIGINE')) return PREVIEW_LABELS.warehouseOrigin;
  if (u.includes('DESTINATION')) return PREVIEW_LABELS.warehouseDestination;
  if (u.includes('COLIS')) return PREVIEW_LABELS.packageDetails;
  if (u.includes('DÉTAILS DOCUMENT') || u.includes('DETAILS DOCUMENT')) return PREVIEW_LABELS.documentDetails;
  return label;
}
